import type { MauiAppLinkInfo, MauiDiagramLinkInfo } from './maui-diagram-paste-plugin';

/**
 * Conversation context encoded in the URL by the "Copy link" button in the
 * AIFC staging area. These params let the paste handler call
 * `finalizeSmartCreate` (FG off) and `resolveConversationAction` (FG on)
 * without needing access to the chat store.
 */
export type RovoConversationContext = {
	conversationId: string;
	invocationId: string;
	messageId: string;
};

/**
 * Single query-param name for the base64-encoded conversation context blob.
 * `isRemixDiagram` remains a separate param for fast detection in the paste
 * plugin without needing to decode.
 */
const CONVO_CONTEXT_PARAM = '_rc';

/**
 * Matches a Confluence whiteboard URL and captures the space key (group 1)
 * and the whiteboard contentId (group 2). Exported via `maui-embed.ts` so
 * downstream consumers (e.g. the native-embeds editor extension) don't have
 * to maintain a parallel copy.
 */
// eslint-disable-next-line require-unicode-regexp
export const WHITEBOARD_PATH_REGEX: RegExp = /\/wiki\/spaces\/([^/]+)\/whiteboard\/(\d+)/;
const REMIX_DIAGRAM_PARAM = 'isRemixDiagram';

/**
 * Matches a MAUI app URL of the form `/remix/ui/{appId}`.
 * The appId can contain alphanumeric characters, hyphens, and underscores.
 */
// eslint-disable-next-line require-unicode-regexp
const MAUI_APP_PATH_REGEX = /\/remix\/ui\/([\w-]+)/;

/**
 * Extracts conversation context from the base64-encoded `_rc` query parameter.
 * Returns undefined if the param is missing or malformed.
 */
function extractConversationContext(urlObj: URL): RovoConversationContext | undefined {
	const encoded = urlObj.searchParams.get(CONVO_CONTEXT_PARAM);
	if (!encoded) {
		return undefined;
	}

	try {
		const json = atob(encoded);
		const parsed = JSON.parse(json);

		const { conversationId, messageId, invocationId } = parsed;
		if (!conversationId || !messageId || !invocationId) {
			return undefined;
		}

		return { conversationId, messageId, invocationId };
	} catch {
		return undefined;
	}
}

/**
 * Strips the `_rc` conversation context query parameter from a URL string.
 */
function stripConversationParams(url: string): string {
	try {
		const urlObj = toURL(url);
		if (!urlObj) {
			return url;
		}
		urlObj.searchParams.delete(CONVO_CONTEXT_PARAM);
		return urlObj.toString();
	} catch {
		return url;
	}
}

/**
 * Determines whether a URL is a MAUI diagram whiteboard link by checking that:
 * 1. The URL matches a Confluence whiteboard path
 * 2. The `isRemixDiagram=true` query parameter is present
 *
 * Also extracts any embedded conversation context from `_r*` query params.
 */
export function parseMauiDiagramLink(url: string): MauiDiagramLinkInfo | null {
	const trimmedUrl = url.trim();
	if (!trimmedUrl) {
		return null;
	}

	const pathMatch = WHITEBOARD_PATH_REGEX.exec(trimmedUrl);
	if (!pathMatch) {
		return null;
	}

	const contentId = pathMatch[2];

	try {
		const urlObj = toURL(trimmedUrl);
		if (!urlObj) {
			return null;
		}

		if (urlObj.searchParams.get(REMIX_DIAGRAM_PARAM) !== 'true') {
			return null;
		}

		return {
			contentId,
			originalUrl: trimmedUrl,
			conversationContext: extractConversationContext(urlObj),
		};
	} catch {
		return null;
	}
}

/**
 * Replaces the space key in a whiteboard URL with the target space key
 * and strips the `isRemixDiagram` query parameter and any `_r*`
 * conversation context parameters.
 */
export function buildMovedUrl(url: string, targetSpaceKey: string): string {
	try {
		const replaced = url.replace(
			WHITEBOARD_PATH_REGEX,
			`/wiki/spaces/${encodeURIComponent(targetSpaceKey)}/whiteboard/$2`,
		);
		return stripConversationParams(stripRemixDiagramParam(replaced));
	} catch {
		return url;
	}
}

/**
 * Strips the `isRemixDiagram` query parameter from a URL.
 */
function stripRemixDiagramParam(url: string): string {
	try {
		const urlObj = toURL(url);
		if (!urlObj) {
			return url;
		}
		urlObj.searchParams.delete(REMIX_DIAGRAM_PARAM);
		return urlObj.toString();
	} catch {
		return url;
	}
}

function toURL(urlString: string): URL | null {
	try {
		return new URL(urlString);
	} catch {
		try {
			return new URL(urlString, window.location.origin);
		} catch {
			return null;
		}
	}
}

/**
 * Determines whether a URL is a MAUI app link by checking that the URL
 * matches a `/remix/ui/{appId}` path.
 */
export function parseMauiAppLink(url: string): MauiAppLinkInfo | null {
	const trimmedUrl = url.trim();
	if (!trimmedUrl) {
		return null;
	}

	const pathMatch = MAUI_APP_PATH_REGEX.exec(trimmedUrl);
	if (!pathMatch) {
		return null;
	}

	return {
		appId: pathMatch[1],
		originalUrl: trimmedUrl,
	};
}

/**
 * Replaces the MAUI app ID in a `/remix/ui/{appId}` URL with a new app ID.
 */
export function buildDuplicatedMauiAppUrl(url: string, newAppId: string): string {
	try {
		return url.replace(MAUI_APP_PATH_REGEX, `/remix/ui/${encodeURIComponent(newAppId)}`);
	} catch {
		return url;
	}
}

/**
 * Extracts the plain text URL from a paste event's clipboard data.
 * Returns null if no text content is available.
 */
export function extractPastedUrl(clipboardData: DataTransfer): string | null {
	const text = clipboardData.getData('text/plain');
	if (!text) {
		return null;
	}

	const trimmed = text.trim();
	if (trimmed.includes('\n') || trimmed.includes('\r')) {
		return null;
	}

	if (
		!trimmed.startsWith('http://') &&
		!trimmed.startsWith('https://') &&
		!trimmed.startsWith('/wiki/') &&
		!trimmed.startsWith('/remix/')
	) {
		return null;
	}

	return trimmed;
}
